import React, { useEffect } from 'react';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { FormField, TextInput } from '@workday/canvas-kit-react';
import styles from './create-account.module.scss';
import { autoId, CardView, emailRegex, formFieldErrorProps } from '@workday/upc-common';
import { ID_EMAIL, ID_LOOKUP_INVITATION_BUTTON } from '../../../autoIds';
import { useForm } from 'react-hook-form';
import { ACCOUNT_DEFAULT_USER } from '../../../nx-environment-variables';
import { INVITATION_PATH, LOGIN_PATH } from '../../../react-router-paths';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEnrollmentInvitationMutation } from '../../../slices/auth-api.slice';

export function CreateAccount() {
    const { t } = useTranslation('auth');
    const history = useHistory();

    const { formState, handleSubmit, register } = useForm({
        defaultValues: {
            [ID_EMAIL]: ACCOUNT_DEFAULT_USER
        },
        reValidateMode: 'onSubmit'
    });

    const { ref: emailInputRef, ...emailInputProps } = register(ID_EMAIL, {
        required: t('Required.fieldGeneric', { ns: 'shared' }),
        pattern: {
            value: emailRegex,
            message: t('LoginEmailEntry.validationError')
        }
    });

    const [enrollmentInvitation, enrollmentInvitationMutation] = useEnrollmentInvitationMutation();

    useEffect(() => {
        if (enrollmentInvitationMutation.isSuccess) {
            history.replace(INVITATION_PATH);
        }
    }, [history, enrollmentInvitationMutation.isSuccess]);

    const onGoBack = () => {
        history.replace(LOGIN_PATH);
    };

    function onSubmit({ email }) {
        enrollmentInvitation({ email });
    }

    return (
        <div className={styles.createAccount}>
            <h1 className={styles.title}>{t('CreateAccount.title')}</h1>
            <h2 className={styles.subTitle}>{t('CreateAccount.description')}</h2>
            <CardView className={styles.card}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <FormField
                        className={styles.email}
                        grow={true}
                        inputId={ID_EMAIL}
                        label={t('CreateAccount.emailLabel')}
                        {...formFieldErrorProps(formState.errors, ID_EMAIL)}>
                        <TextInput
                            {...autoId(ID_EMAIL)}
                            {...emailInputProps}
                            autoComplete='username'
                            type='email'
                            autoFocus={true}
                            disabled={enrollmentInvitationMutation.isLoading}
                            id={ID_EMAIL}
                            placeholder={t('CreateAccount.emailInputPlaceholder')}
                            ref={emailInputRef}
                        />
                    </FormField>
                    <PrimaryButton
                        {...autoId(ID_LOOKUP_INVITATION_BUTTON)}
                        className={styles.button}
                        disabled={enrollmentInvitationMutation.isLoading}
                        grow={true}
                        size='large'
                        type='submit'>
                        {t('CreateAccount.submitButton')}
                    </PrimaryButton>
                </form>
            </CardView>
            <div className={styles.bottomContainer}>
                {t('CreateAccount.alreadyHave')}{' '}
                <TertiaryButton type='button' className={styles.button} size='large' onClick={onGoBack}>
                    {t('CreateAccount.signInButton')}
                </TertiaryButton>
            </div>
        </div>
    );
}
